/**
 * Input Component
 * Reusable input component with validation and error handling
 */

import React, { forwardRef } from 'react';
import { type FormFieldProps } from '../../types';
import { useSafeThemeClasses } from '@/shared/theme';

interface InputProps extends FormFieldProps, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'bordered' | 'ghost';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  required = false,
  disabled = false,
  placeholder,
  helperText,
  size = 'md',
  variant = 'default',
  leftIcon,
  rightIcon,
  className = '',
  ...props
}, ref) => {
  const theme = useSafeThemeClasses();
  
  const baseClasses = 'w-full transition-all duration-200 focus:outline-none placeholder:opacity-60';
  
  const variantClasses = {
    default: 'input-bordered',
    bordered: 'input-bordered',
    ghost: 'input-ghost',
  };

  const sizeClasses = {
    sm: 'input-sm px-3 py-1.5 text-sm leading-normal',
    md: 'px-4 py-3 text-base leading-normal',
    lg: 'input-lg px-5 py-4 text-lg leading-normal',
  };

  const inputClasses = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    error ? 'input-error' : '',
    disabled ? 'input-disabled opacity-60' : '',
    leftIcon ? 'pl-10' : '',
    rightIcon ? 'pr-10' : '',
    theme.textPrimary, // Add theme text color for input text
    theme.placeholderText,
    'align-middle', // Ensure proper vertical alignment
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className="form-control w-full">
      {label && (
        <label className="label py-1">
          <span className={`label-text ${theme.textLabel} text-sm`}>
            {label}
            {required && <span className={`${theme.textError} ml-1`}>*</span>}
          </span>
        </label>
      )}
      
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
            <span className={`${theme.textMuted} text-sm`}>{leftIcon}</span>
          </div>
        )}
        
        <input
          ref={ref}
          className={inputClasses}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          {...props}
        />
        
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none z-10">
            <span className={`${theme.textMuted} text-sm`}>{rightIcon}</span>
          </div>
        )}
      </div>
      
      {(error || helperText) && (
        <label className="label py-1">
          <span className={`label-text-alt text-xs mt-1 ${error ? theme.textError : theme.textMuted}`}>
            {error ?? helperText}
          </span>
        </label>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
